// AudioManager — adaptive-audio engine (singleton).
//
// Two mutually-exclusive audible paths, both silent unless their gate opens:
//   • ordinary player path — gated by the global ADAPTIVE_AUDIO_ENABLED flag (still OFF, so
//     ordinary players/admins are ALWAYS silent).
//   • founder audible-preview path — gated by a backend-issued, expiring entitlement
//     (`previewAuthorized`) plus explicit opt-in, an explicit "Start Audio Preview" gesture,
//     online status, bound local allowlisted assets, and no emergency silence.
//
// An AudioContext is created ONLY inside ensureContext(), which is only reachable after
// startPreview() (an explicit user gesture). Never on load / auth / route navigation.
// Only local, server-allowlisted tracks are ever loaded (fetched from /api/audio/track/{id}).

const DEFAULTS = {
  music_enabled: false, music_volume: 0.6, effects_volume: 0.7, mute_all: false,
  calm_mode: false, reduced_intensity: false, dynamic_tension_enabled: true,
  beat_sync_enabled: false, preferred_intensity: "standard",
  reduced_bass: false, reduced_high_freq: false, mono_mix: false,
  crew_motifs_enabled: true, crew_motif_volume: 0.6,
  reduce_ceremonial_motifs: false, disable_injury_motifs: false,
};
const MAX_DIAG = 60;
const MAX_CREW = 3;               // 1 primary + up to 2 supporting crew fragments
const MASTER_CAP = 0.85;          // safe initial maximum preview volume
const FADE_MS = 1200;
const OFFLINE_FADE_MS = 600;

class AudioManager {
  constructor() {
    if (AudioManager._instance) return AudioManager._instance;
    this.ctx = null;
    this.masterGain = null;
    this.buffers = {};              // track_id -> AudioBuffer (lazy cache)
    this.active = {};               // track_id -> {source, gain} (one loop per stem)
    this.crew = {};                 // `${crew_id}:${role}` -> crew-motif track (lazy)
    this.activeCrew = [];           // layered crew-motif fragments (bounded to MAX_CREW)
    this.lastCrewMotif = null;
    this.manifest = [];             // authorized client manifest tracks
    this.flags = { ADAPTIVE_AUDIO_ENABLED: false, PLAYER_MOTIFS_ENABLED: false, BEAT_SYNC_UI_ENABLED: false };
    this.settings = { ...DEFAULTS };
    this.previewAuthorized = false;
    this.emergency = false;
    this.userInteracted = false;
    this.previewStarted = false;
    this.online = true;
    this.previewVolume = 0.6;       // founder master preview volume (0..1, capped)
    this.currentState = null;
    this.currentExpedition = null;
    this.currentBpm = null;
    this.currentIntensity = null;
    this.lastTransition = null;
    this.awaitingRestore = false;
    this.playedCues = new Set();    // one-time cue dedup (per session)
    this._onDiag = null;
    this.diagnostics = {
      contextCreated: false, soundProduced: false, assetsBound: false,
      transitions: [], loadFailures: 0, transitionFailures: 0,
    };
    AudioManager._instance = this;
  }

  onDiag(cb) { this._onDiag = cb; }
  _emitDiag() { if (this._onDiag) try { this._onDiag(this.getDiagnostics()); } catch (e) { /* noop */ } }

  configure(flags) { this.flags = { ...this.flags, ...(flags || {}) }; return this.flags; }

  applySettings(s) {
    this.settings = { ...this.settings, ...(s || {}) };
    if (!this.settings.music_enabled || this.settings.mute_all) this.suspend();
    else this._applyGains();
    return this.settings;
  }

  setPreviewAuth(authorized, emergency) {
    this.previewAuthorized = !!authorized;
    this.emergency = !!emergency;
    if (!this.canActivate()) this.stopAll(true);
    this._emitDiag();
  }

  setManifest(tracks) {
    this.manifest = Array.isArray(tracks) ? tracks : [];
    this.diagnostics.assetsBound = this.manifest.length > 0;
    this._emitDiag();
  }

  // Crew motifs are loaded via the dedicated authorized endpoint (entitlement-gated).
  setCrewManifest(tracks) {
    this.crew = {};
    (Array.isArray(tracks) ? tracks : []).forEach((t) => {
      if (t.crew_id && t.motif_role) this.crew[`${t.crew_id}:${t.motif_role}`] = t;
    });
    this._emitDiag();
  }
  _crewTrack(crewId, role) {
    return this.crew[`${crewId}:${role}`] || this.crew[`${crewId}:base`] || this.crew[`${crewId}:calm`] || null;
  }

  // Layer a short crew motif over the current base soundtrack. Descriptive only; never blocks
  // the base soundtrack and always degrades to silence on any failure.
  async playCrewMotif(crewId, role, opts = {}) {
    const s = this.settings;
    if (!this.canActivate()) return { played: false, reason: "not_activatable" };
    if (s.crew_motifs_enabled === false) return { played: false, reason: "crew_disabled" };
    if (role === "injury" && s.disable_injury_motifs) return { played: false, reason: "injury_opt_out" };
    let track = this._crewTrack(crewId, role);
    if (s.calm_mode && track && !track.calm_mode_eligible) track = this._crewTrack(crewId, "calm") || track;
    if (!track) return { played: false, reason: "no_track" };
    const ctx = this.ensureContext();
    if (!ctx) return { played: false, reason: "no_context" };
    if (ctx.state === "suspended") { try { await ctx.resume(); } catch (e) { /* noop */ } }
    const buf = await this._load(track);
    if (!buf) return { played: false, reason: "load_failed", fallback: true };
    if (!this.canActivate()) return { played: false, reason: "not_activatable" };
    while (this.activeCrew.length >= MAX_CREW) { const old = this.activeCrew.shift(); this._fadeCrew(old); }
    let g = (s.crew_motif_volume ?? 0.6);
    if (opts.supporting) g *= 0.55;                          // supporting fragments sit under the primary
    if (role === "defining" && s.reduce_ceremonial_motifs) g *= 0.5;
    if (s.reduced_intensity) g *= 0.75;
    const src = ctx.createBufferSource();
    src.buffer = buf; src.loop = false;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain); gain.connect(this.masterGain);
    src.start();
    gain.gain.setTargetAtTime(g, ctx.currentTime, 0.08);
    const entry = { crewId, role, gain, source: src, at: Date.now() };
    this.activeCrew.push(entry);
    this.lastCrewMotif = { crewId, role, at: Date.now() };
    this.diagnostics.soundProduced = true;
    const cleanup = () => { this.activeCrew = this.activeCrew.filter((e) => e !== entry); this._emitDiag(); };
    src.onended = cleanup;
    setTimeout(cleanup, Math.round((buf.duration + 0.4) * 1000));
    this._emitDiag();
    return { played: true, track_id: track.track_id, role };
  }
  _fadeCrew(entry) {
    if (!entry || !this.ctx) return;
    try {
      entry.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.12);
      setTimeout(() => { try { entry.source.stop(); } catch (e) { /* noop */ } }, 400);
    } catch (e) { /* noop */ }
  }
  stopAllCrew() { const list = this.activeCrew; this.activeCrew = []; list.forEach((e) => this._fadeCrew(e)); this._emitDiag(); }

  setOnline(online) { this.online = !!online; }
  markInteraction() { this.userInteracted = true; }
  setPreviewVolume(v) {
    this.previewVolume = Math.max(0, Math.min(1, v));
    this._applyGains();
    this._emitDiag();
  }

  canActivate() {
    return (
      this.previewAuthorized === true &&
      this.emergency !== true &&
      this.settings.music_enabled === true &&
      this.settings.mute_all !== true &&
      this.userInteracted === true &&
      this.previewStarted === true &&
      this.online === true &&
      this.diagnostics.assetsBound === true
    );
  }

  // Explicit user-gesture entry point — the ONLY way audio becomes audible. Pressing this
  // button IS the explicit opt-in (sets music_enabled) required by the audible-preview gate.
  async startPreview() {
    if (this.previewAuthorized !== true || this.emergency === true) return { ok: false, reason: "not_authorized" };
    this.userInteracted = true;
    this.previewStarted = true;
    this.settings.music_enabled = true;   // opt-in via the gesture
    this.settings.mute_all = false;
    const ctx = this.ensureContext();
    if (!ctx) return { ok: false, reason: "no_context" };
    if (ctx.state === "suspended") { try { await ctx.resume(); } catch (e) { /* noop */ } }
    this._emitDiag();
    return { ok: true };
  }

  stopPreview() {
    this.previewStarted = false;
    this.stopAll(true);
    this.suspend();
    this._emitDiag();
  }

  ensureContext() {
    if (!this.canActivate()) return null;
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this._masterLevel();
      this.masterGain.connect(this.ctx.destination);
      this.diagnostics.contextCreated = true;
    }
    return this.ctx;
  }

  _masterLevel() {
    const v = Math.min(this.previewVolume, MASTER_CAP) * (this.settings.music_volume ?? 0.6);
    return this.settings.mute_all ? 0 : v;
  }

  _applyGains() {
    if (this.masterGain && this.ctx) {
      const now = this.ctx.currentTime;
      this.masterGain.gain.setTargetAtTime(this._masterLevel(), now, 0.05);
    }
  }

  async _load(track) {
    if (this.buffers[track.track_id]) return this.buffers[track.track_id];
    try {
      const token = localStorage.getItem("ff_token");
      const url = (process.env.REACT_APP_BACKEND_URL || "") + track.stream_url;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("http_" + res.status);
      const arr = await res.arrayBuffer();
      const buf = await this.ctx.decodeAudioData(arr);
      this.buffers[track.track_id] = buf;
      return buf;
    } catch (e) {
      this.diagnostics.loadFailures += 1;
      this._emitDiag();
      return null;
    }
  }

  _stemsFor(state, expedition) {
    const s = this.settings;
    let all = this.manifest.filter((t) => t.state === state);
    let list;
    if (state === "expedition_exploration" && expedition) {
      list = all.filter((t) => t.expedition === expedition);
      if (list.length === 0) list = all.filter((t) => !t.expedition);
    } else {
      list = all.filter((t) => !t.expedition);
      if (list.length === 0) list = all;
    }
    return list.filter((t) => {
      if (s.calm_mode && !t.calm_mode_eligible) return false;
      if (s.reduced_intensity && !t.reduced_intensity_eligible) return false;
      if (s.dynamic_tension_enabled === false && t.stem_type === "tension") return false;
      return true;
    });
  }

  _stemGain(track) {
    let g = 1.0;
    if (this.settings.reduced_intensity) g *= 0.7;
    if (track.stem_type === "tension") g *= 0.8;
    return g;
  }

  async _ensureLoop(track) {
    if (this.active[track.track_id]) return; // already looping — no duplicate
    const buf = await this._load(track);
    if (!buf || !this.canActivate()) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    if (track.loop_start != null && track.loop_end != null) {
      src.loopStart = track.loop_start; src.loopEnd = track.loop_end;
    }
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain); gain.connect(this.masterGain);
    src.start();
    const target = this._stemGain(track);
    gain.gain.setTargetAtTime(target, this.ctx.currentTime, FADE_MS / 3000);
    this.active[track.track_id] = { source: src, gain };
    this.diagnostics.soundProduced = true;
  }

  _stopStem(id, fadeMs = FADE_MS) {
    const a = this.active[id];
    if (!a) return;
    delete this.active[id];
    try {
      const t = this.ctx.currentTime;
      a.gain.gain.setTargetAtTime(0, t, fadeMs / 3000);
      setTimeout(() => { try { a.source.stop(); } catch (e) { /* noop */ } }, fadeMs + 120);
    } catch (e) { /* noop */ }
  }

  async _playOneShot(track) {
    const buf = await this._load(track);
    if (!buf || !this.canActivate()) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf; src.loop = false;
    const gain = this.ctx.createGain();
    gain.gain.value = this._stemGain(track);
    src.connect(gain); gain.connect(this.masterGain);
    src.start();
    this.diagnostics.soundProduced = true;
  }

  // Play/crossfade to a musical state. `skipOneShots` avoids replaying one-time cues on reconnect.
  async playState(state, expedition, opts = {}) {
    const skipOneShots = !!opts.skipOneShots;
    this.transitionTo(state, expedition);
    if (!this.canActivate()) return { played: false, reason: "not_activatable" };
    const ctx = this.ensureContext();
    if (!ctx) return { played: false, reason: "no_context" };
    if (ctx.state === "suspended") { try { await ctx.resume(); } catch (e) { /* noop */ } }
    try {
      const targets = this._stemsFor(state, expedition);
      const loops = targets.filter((t) => t.loop);
      const oneShots = targets.filter((t) => !t.loop);
      const keep = new Set(loops.map((t) => t.track_id));
      Object.keys(this.active).forEach((id) => { if (!keep.has(id)) this._stopStem(id); });
      for (const t of loops) await this._ensureLoop(t);
      for (const t of oneShots) {
        const token = t.track_id;
        if (skipOneShots || this.playedCues.has(token)) continue;
        this.playedCues.add(token);
        await this._playOneShot(t);
      }
      this.currentBpm = (targets[0] && targets[0].bpm) || this.currentBpm;
      this.currentIntensity = (targets[0] && targets[0].intensity_range) || null;
      this._emitDiag();
      return { played: true, stems: loops.map((t) => t.stem_type) };
    } catch (e) {
      this.diagnostics.transitionFailures += 1;
      this._emitDiag();
      return { played: false, reason: "error" };
    }
  }

  transitionTo(state, expedition, resolution) {
    const entry = { from: this.currentState, to: state, expedition: expedition || null, at: Date.now(), activatable: this.canActivate() };
    this.diagnostics.transitions.push(entry);
    if (this.diagnostics.transitions.length > MAX_DIAG) this.diagnostics.transitions.shift();
    this.currentState = state;
    this.currentExpedition = expedition || null;
    this.lastTransition = entry;
    return entry;
  }

  // Offline: fade music toward silence quickly; stop safety/result audio immediately.
  fadeOffline() {
    this.awaitingRestore = true;
    this.stopAllCrew();
    Object.keys(this.active).forEach((id) => this._stopStem(id, OFFLINE_FADE_MS));
    this._emitDiag();
  }

  // Reconnect: caller must first revalidate entitlement + authoritative state, then restore.
  async restoreOnline(state, expedition) {
    this.awaitingRestore = false;
    if (!this.canActivate()) return { restored: false };
    return this.playState(state, expedition, { skipOneShots: true });
  }

  suspend() { if (this.ctx && this.ctx.state === "running") this.ctx.suspend().catch(() => {}); }
  async resume() { if (this.ctx && this.ctx.state === "suspended" && this.canActivate()) { try { await this.ctx.resume(); } catch (e) { /* noop */ } } }

  stopAll(immediate = false) {
    this.stopAllCrew();
    Object.keys(this.active).forEach((id) => this._stopStem(id, immediate ? 0 : FADE_MS));
  }

  clearCache() { this.buffers = {}; this.playedCues = new Set(); this._emitDiag(); }

  resetSettings() { this.settings = { ...DEFAULTS }; this.stopAll(true); this.suspend(); this._emitDiag(); return this.settings; }

  getDiagnostics() {
    return {
      ...this.diagnostics,
      flags: { ...this.flags },
      settings: { ...this.settings },
      previewAuthorized: this.previewAuthorized,
      emergency: this.emergency,
      previewStarted: this.previewStarted,
      userInteracted: this.userInteracted,
      online: this.online,
      awaitingRestore: this.awaitingRestore,
      currentState: this.currentState,
      currentExpedition: this.currentExpedition,
      currentBpm: this.currentBpm,
      currentIntensity: this.currentIntensity,
      lastTransition: this.lastTransition,
      activeStems: Object.keys(this.active),
      activeCrewMotifs: this.activeCrew.map((e) => ({ crewId: e.crewId, role: e.role })),
      lastCrewMotif: this.lastCrewMotif,
      crewTracksBound: Object.keys(this.crew).length,
      manifestTracks: this.manifest.length,
      canActivate: this.canActivate(),
      contextState: this.ctx ? this.ctx.state : "none",
      previewVolume: this.previewVolume,
    };
  }

  teardown() {
    this.stopAll(true);
    if (this.ctx) { try { this.ctx.close(); } catch (e) { /* noop */ } this.ctx = null; }
    this.masterGain = null; this.active = {};
  }
}

export const audioManager = new AudioManager();
export default audioManager;
